import cx from 'classnames'
import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import IconCheck from '@adv-mt/mt-svg-icons/lib/Check.js'

import {BASE_CLASS, CHECK_ELEMENT_TYPES, DIRECTIONS, NO_OP} from '../../config.js'
import CheckElement from '../CheckElement/index.js'

const ItemBasicContent = ({
  attributes = [],
  checkElementType = CHECK_ELEMENT_TYPES.NONE,
  description,
  direction,
  element,
  extendedInfo,
  hasIconCheck = true,
  icon: Icon,
  id,
  imageSrc,
  isBolder,
  isFullWidth,
  isHighlighted = false,
  isIndeterminate,
  isLeftIcon = false,
  isSelected,
  label,
  onChange = NO_OP,
  rightIcon: RightIcon
}) => {
  const hasIcon = Boolean(Icon)
  const hasElement = Boolean(element)
  const hasExtendedInfo = Boolean(extendedInfo)
  const hasRightIcon = Boolean(RightIcon)
  const hasCheckElement = Boolean(checkElementType !== CHECK_ELEMENT_TYPES.NONE)
  const hasAttributes = Boolean(attributes.length)
  const hasDescription = Boolean(description)

  const iconSize = isLeftIcon ? atomIconSizes.small : atomIconSizes.medium

  const mainClassNames = cx(`${BASE_CLASS}Content-main`, {
    [`${BASE_CLASS}Content-main--column`]: direction === DIRECTIONS.COLUMN_REVERSE,
    [`${BASE_CLASS}Content-main--fullWidth`]: isFullWidth
  })

  const filterItemBasicLabelTextWrapperClassNames = cx(`${BASE_CLASS}Content-mainLabelTextWrapper`, {
    [`${BASE_CLASS}Content-mainLabelTextWrapper--column`]: direction === DIRECTIONS.COLUMN_REVERSE,
    [`${BASE_CLASS}Content-mainLabelTextWrapper--rightIcon`]: hasRightIcon
  })

  const filterItemBasicLabelTextClassNames = cx(`${BASE_CLASS}Content-mainLabelText`, {
    [`${BASE_CLASS}Content-mainLabelText--bold`]: isSelected || isHighlighted || isBolder
  })

  const filterItemBasicMainLabelWrapperClassNames = cx(`${BASE_CLASS}Content-mainLabelWrapper`, {
    [`${BASE_CLASS}Content-mainLabelWrapper--rowReverse`]: isLeftIcon && !hasIcon
  })

  return (
    <>
      <div className={mainClassNames}>
        {hasCheckElement && (
          <CheckElement
            id={id}
            imageSrc={imageSrc}
            isChecked={isSelected}
            isIndeterminate={isIndeterminate}
            label={label}
            onChange={onChange}
            type={checkElementType}
          />
        )}
        <div className={filterItemBasicMainLabelWrapperClassNames}>
          {hasIcon && (
            <div className={`${BASE_CLASS}Content-mainLabelIcon`}>
              <Icon size={atomIconSizes.medium} />
            </div>
          )}
          {hasElement && <div className={`${BASE_CLASS}Content-mainLabelIcon`}>{element}</div>}
          <div className={filterItemBasicLabelTextWrapperClassNames}>
            <span className={filterItemBasicLabelTextClassNames}>{label}</span>
            {hasExtendedInfo && <span className={`${BASE_CLASS}Content-mainLabelExtendedInfo`}>{extendedInfo}</span>}
            {hasRightIcon && (
              <div className={`${BASE_CLASS}Content-mainLabelRightIcon`}>
                {typeof RightIcon === 'function' ? <RightIcon /> : RightIcon}
              </div>
            )}
          </div>
          {!hasCheckElement && isSelected && hasIconCheck && <IconCheck size={iconSize} />}
        </div>
      </div>
      {hasAttributes && (
        <ul className={`${BASE_CLASS}Content-attributes`}>
          {attributes.map(({key, value}, index) => (
            <li key={key + index} className={`${BASE_CLASS}Content-attributesItem`}>
              <span className={`${BASE_CLASS}Content-attributesItemKey`}>{key}</span>
              <span className={`${BASE_CLASS}Content-attributesItemValue`}>{value}</span>
            </li>
          ))}
        </ul>
      )}
      {hasDescription && <p className={`${BASE_CLASS}Content-description`}>{description}</p>}
    </>
  )
}

ItemBasicContent.displayName = 'ItemBasicContent'
ItemBasicContent.propTypes = {
  attributes: PropTypes.arrayOf(PropTypes.shape({key: PropTypes.string, value: PropTypes.string})),
  checkElementType: PropTypes.string,
  description: PropTypes.string,
  direction: PropTypes.string,
  element: PropTypes.node,
  extendedInfo: PropTypes.string,
  hasIconCheck: PropTypes.bool,
  icon: PropTypes.func,
  id: PropTypes.string,
  imageSrc: PropTypes.string,
  isBolder: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  isLeftIcon: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  rightIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
}

export default ItemBasicContent
